import React, { useEffect } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useBillingPricingConfig } from '../../../services/billing.service';
import { computePricing, marginAt } from '../../../utils/pricingCalculator';

interface Props {
  credits: number;
  advanceCredits?: number;
  priceUsd: number;
  kind: 'package' | 'pack';
  onBreakEvenChange?: (value: number | null) => void;
  onUseSuggestedPrice?: (value: number) => void;
}
const usd = (value: number) => `$${value.toFixed(2)}`;
export const PricingCalculatorPanel: React.FC<Props> = ({
  credits,
  advanceCredits = 0,
  priceUsd,
  kind,
  onBreakEvenChange,
  onUseSuggestedPrice,
}) => {
  const { data: config, isLoading, error } = useBillingPricingConfig();
  useEffect(() => {
    if (!config) {
      onBreakEvenChange?.(null);
      return;
    }
    const rate = kind === 'package' ? config.minUtilization : 1;
    onBreakEvenChange?.(
      computePricing({
        credits: credits + advanceCredits,
        utilization: rate,
        config,
        vndPerUsd: config.vndPerUsd,
      }).breakEvenUsd
    );
  }, [advanceCredits, config, credits, kind, onBreakEvenChange]);
  if (isLoading) return <Typography variant="body2">Loading pricing configuration…</Typography>;
  if (!config || error)
    return <Alert severity="warning">Pricing configuration is unavailable.</Alert>;
  const total = credits + advanceCredits;
  const utilization = kind === 'package' ? config.minUtilization : 1;
  const result = computePricing({
    credits: total,
    utilization,
    config,
    vndPerUsd: config.vndPerUsd,
  });
  const margin = (rate: number) =>
    marginAt({ priceUsd, credits: total, utilization: rate, config });
  const marginColor = (value: number) =>
    value < 0 ? 'error.main' : value < config.targetMargin ? 'warning.main' : 'success.main';
  return (
    <Box className="space-y-2 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
      <Typography variant="subtitle2">Pricing estimate</Typography>
      <Typography variant="body2">Total credits: {total.toLocaleString('en-US')}</Typography>
      <Typography variant="body2">
        Break-even: {usd(result.breakEvenUsd)}
        {result.suggestedVnd !== null && config.vndPerUsd
          ? ` / ₫${Math.round(result.breakEvenUsd * config.vndPerUsd * (1 + config.fxBuffer)).toLocaleString('en-US')}`
          : ''}
      </Typography>
      <Typography variant="body2">
        Suggested: {usd(result.suggestedUsd)}
        {result.suggestedVnd !== null
          ? ` / ₫${Math.round(result.suggestedVnd).toLocaleString('en-US')}`
          : ''}
      </Typography>
      <Typography variant="body2" sx={{ color: marginColor(margin(utilization)) }}>
        Margin @ {Math.round(utilization * 100)}%: {(margin(utilization) * 100).toFixed(1)}%
      </Typography>
      {kind === 'package' && (
        <Typography variant="body2" sx={{ color: marginColor(margin(1)) }}>
          Margin @ 100%: {(margin(1) * 100).toFixed(1)}%
        </Typography>
      )}
      {config.unitCosts.length ? (
        <Box>
          {config.unitCosts.map((unit) => (
            <Typography key={unit.key} variant="caption" display="block">
              ≈{' '}
              {(total / config.creditsPerUsd / unit.avgCostUsd).toLocaleString('en-US', {
                maximumFractionDigits: 0,
              })}{' '}
              {unit.label}
            </Typography>
          ))}
        </Box>
      ) : (
        <Alert severity="info">Unit cost data is not available yet.</Alert>
      )}
      {!config.vndPerUsd && (
        <Alert severity="info">Exchange rate is unavailable. Prices are shown in USD only.</Alert>
      )}
      <Button
        size="small"
        variant="outlined"
        onClick={() => onUseSuggestedPrice?.(result.suggestedUsd)}
      >
        Use suggested price
      </Button>
    </Box>
  );
};
